import { useState, useCallback, useMemo, useRef } from 'react';
import type { TableColumn } from '../components/ui/tableTemplate/TableTemplateTypes';
import { DEFAULT_MAX_COLUMNS } from '../shared/constants';

function loadSavedOrder(storageKey: string): string[] | null {
  try {
    const raw = localStorage.getItem(storageKey);
    if (!raw) return null;
    const parsed: unknown = JSON.parse(raw);
    if (Array.isArray(parsed) && parsed.every(x => typeof x === 'string')) return parsed as string[];
    return null;
  } catch {
    return null;
  }
}

export interface UseColumnConfiguratorReturn<T> {
  visibleColumns: TableColumn<T>[];
  visibleKeys: Set<string>;
  visibleOrder: string[];
  isOpen: boolean;
  openConfigurator: () => void;
  cancelConfigurator: () => void;
  applyConfigurator: () => void;
  saveViewConfigurator: () => void;
  toggleColumn: (key: string) => void;
  resetToDefaults: () => void;
  selectedCount: number;
  effectiveMax: number;
  isAtMax: boolean;
}

export interface UseColumnConfiguratorOptions {
  /**
   * localStorage key used to persist the user's column order.
   * Must be unique per table — two tables sharing a key would overwrite each other.
   * Example: `'homere:employees:columnOrder'`, `'homere:contracts:columnOrder'`
   */
  storageKey: string;
  /**
   * Maximum number of configurable columns the user can select.
   * Defaults to DEFAULT_MAX_COLUMNS.
   */
  maxColumns?: number;
}

/**
 * Manages the visible column set for a configurable table.
 * Reusable across any feature — pass the correct `storageKey` to keep
 * each table's saved preferences isolated.
 *
 * - `defaultVisible: true` on a column → included in the initial visible set.
 * - `configurable: false` on a column → always shown, never in the picker.
 */
export function useColumnConfigurator<T>(
  allColumns: TableColumn<T>[],
  { storageKey, maxColumns = DEFAULT_MAX_COLUMNS }: UseColumnConfiguratorOptions,
): UseColumnConfiguratorReturn<T> {
  const [isOpen, setIsOpen] = useState(false);

  const { allKeys, nonConfigurableKeys, defaultVisibleKeys } = useMemo(() => {
    const allKeys = new Set(allColumns.map(col => String(col.key)));
    const nonConfigurableKeys = new Set(
      allColumns.filter(col => col.configurable === false).map(col => String(col.key)),
    );
    const defaultVisibleKeys = allColumns
      .filter(col => col.configurable !== false && col.defaultVisible === true)
      .map(col => String(col.key));
    return { allKeys, nonConfigurableKeys, defaultVisibleKeys };
  }, [allColumns]);

  const [visibleOrder, setVisibleOrder] = useState<string[]>(() => {
    const saved = loadSavedOrder(storageKey) ?? defaultVisibleKeys;
    return saved.filter(k => allKeys.has(k) && !nonConfigurableKeys.has(k));
  });

  // Snapshot used to restore state on cancel.
  const snapshotRef = useRef<string[]>([...visibleOrder]);

  const visibleKeys = useMemo(() => new Set(visibleOrder), [visibleOrder]);

  const visibleColumns = useMemo(() => {
    const nonConfigurable = allColumns.filter(col => col.configurable === false);
    const configurable = visibleOrder
      .filter(key => !nonConfigurableKeys.has(key))
      .map(key => allColumns.find(col => String(col.key) === key))
      .filter((c): c is TableColumn<T> => c !== undefined);
    return [...nonConfigurable, ...configurable];
  }, [allColumns, visibleOrder, nonConfigurableKeys]);

  const nonConfigurableCount = allColumns.filter(col => col.configurable === false).length;
  // maxColumns is the total displayed column budget (configurable + non-configurable).
  // Non-configurable columns always consume slots, so configurables are capped at the remainder.
  const maxConfigurable = Math.max(0, maxColumns - nonConfigurableCount);
  const selectedCount = visibleOrder.length + nonConfigurableCount;
  const effectiveMax = maxColumns;
  const isAtMax = visibleOrder.length >= maxConfigurable;

  const openConfigurator = useCallback(() => {
    snapshotRef.current = [...visibleOrder];
    setIsOpen(true);
  }, [visibleOrder]);

  const cancelConfigurator = useCallback(() => {
    setVisibleOrder([...snapshotRef.current]);
    setIsOpen(false);
  }, []);

  const applyConfigurator = useCallback(() => {
    setIsOpen(false);
  }, []);

  const saveViewConfigurator = useCallback(() => {
    try {
      localStorage.setItem(storageKey, JSON.stringify(visibleOrder));
    } catch {
      // Ignore storage errors silently.
    }
    setIsOpen(false);
  }, [storageKey, visibleOrder]);

  const toggleColumn = useCallback((key: string) => {
    setVisibleOrder(prev => {
      if (prev.includes(key)) {
        if (prev.length > 1) return prev.filter(k => k !== key);
        return prev;
      } else {
        if (prev.length >= maxConfigurable) return prev;
        // Re-insert at the canonical position defined in allColumns so that
        // removing and re-adding a column always restores its original order.
        const canonicalOrder = allColumns
          .filter(col => col.configurable !== false)
          .map(col => String(col.key));
        const next = new Set([...prev, key]);
        return canonicalOrder.filter(k => next.has(k));
      }
    });
  }, [maxConfigurable, allColumns]);

  const resetToDefaults = useCallback(() => {
    setVisibleOrder([...defaultVisibleKeys]);
  }, [defaultVisibleKeys]);

  return {
    visibleColumns,
    visibleKeys,
    visibleOrder,
    isOpen,
    openConfigurator,
    cancelConfigurator,
    applyConfigurator,
    saveViewConfigurator,
    toggleColumn,
    resetToDefaults,
    selectedCount,
    effectiveMax,
    isAtMax,
  };
}
